// =============================================================================
// DIAL CACHE TTL — one home for the Dial Analytics localStorage cache policy
// =============================================================================
// PURPOSE: the range-classifier and every TTL constant used by the four dial
// hooks, so two numbers rendered side by side on one page cannot silently be
// different ages.
//
// WHY THIS FILE EXISTS: `isHistoricalRange` was written out byte-identically in
// useDialAnalytics.ts, useDialerScorecards.ts and useDialerConnects.ts, and the
// cleanup threshold diverged between them (48h) and useDialDueQueue (1h) with
// nothing marking which divergence was deliberate. Extracted 2026-08-04 (phase 0
// of the Dial Analytics architecture build).
//
// SCOPE IS DIAL-ONLY, ON PURPOSE. There are further classifier variants across
// the DM and portal hooks, including one browser-local-vs-calendar-day variant
// that produces a real 2-day disagreement window on DM Health. Unifying those is
// a separate change with its own blast radius — do not widen this file to cover
// them without measuring that first.
//
// NOT a cache-version mechanism. Changing a TTL here does NOT evict anything.
// Stored payload SHAPE changes still require `/cachebump`
// (MANUAL_SCHEMA_VERSION in lib/cacheVersion.ts).
// =============================================================================

/**
 * Is this range entirely in the past?
 *
 * A range ending before today's local midnight can never gain new rows, so it
 * earns the long TTL. Uses BROWSER-local midnight, not org-timezone midnight:
 * the classifier only decides how long to trust a cache entry, and a viewer in a
 * different timezone from the org gets at worst one extra revalidation. Do not
 * "fix" this to org-tz without a reason — it would make the cache key depend on
 * data the hook does not always have at call time.
 *
 * `end === null` (open-ended range) is treated as live, never historical.
 */
export const isHistoricalRange = (end: Date | null): boolean => {
  if (!end) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return end < today;
};

// --- Range-scoped hooks: useDialAnalytics, useDialerScorecards, useDialerConnects

/** Live ranges (any range including today) — today's dials still arrive. */
export const DIAL_CACHE_TTL = 15 * 60 * 1000;

/** Closed ranges — the rows are final, so a long TTL is safe. */
export const DIAL_HISTORICAL_CACHE_TTL = 24 * 60 * 60 * 1000;

/** Evict on sweep past this age. 2x the historical TTL, matching prior behaviour. */
export const DIAL_CLEANUP_THRESHOLD = DIAL_HISTORICAL_CACHE_TTL * 2;

/** The TTL a range-scoped dial hook should apply for a given range end. */
export const dialCacheTtl = (end: Date | null): number =>
  isHistoricalRange(end) ? DIAL_HISTORICAL_CACHE_TTL : DIAL_CACHE_TTL;

// --- Now-anchored hook: useDialDueQueue
// DELIBERATE DEVIATION, do not unify with the constants above. The due queue is
// anchored to now(), not to the page date range, so a 24h historical tier would
// freeze the reminders it exists to surface (the G113 stale-zeros class). Flat
// 2-min TTL, and anything over an hour old is garbage rather than merely stale.

/** Flat TTL for the now-anchored due queue. No historical tier by design. */
export const DIAL_DUE_CACHE_TTL = 2 * 60 * 1000;

/** Due-queue entries older than this are dropped outright on cleanup. */
export const DIAL_DUE_CLEANUP_THRESHOLD = 60 * 60 * 1000;
